import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Gift, Store, Flag, Inbox, MapPin } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';

const CatIcon = ({ category, className }) =>
  category === 'yard_sale' ? <Store className={className} /> : <Gift className={className} />;

export default function NotificationBell({ onSelectPin }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get('/me/alerts').then(r => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const handleOpenChange = (o) => {
    setOpen(o);
    if (o) {
      api.post('/me/alerts/seen').catch(() => {});
      setData(d => (d ? { ...d, unread_count: 0 } : d));
    }
  };

  const clearAll = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    api.post('/me/alerts/clear').catch(() => {});
    setData(d => (d ? { ...d, notifications: [], nearby: [], unread_count: (d.staff_tickets || []).length } : d));
  };

  const count = data?.unread_count || 0;
  const nearby = data?.nearby || [];
  const tickets = data?.staff_tickets || [];
  const notifications = data?.notifications || [];
  const hasAny = nearby.length || tickets.length || notifications.length;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell-btn"
          aria-label="Notifications"
          className="relative glass-panel rounded-full w-11 h-11 flex items-center justify-center hover:-translate-y-0.5 transition"
        >
          <Bell className="w-5 h-5 text-brand-ink" />
          {count > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#C86F53] text-white text-[11px] font-bold flex items-center justify-center"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl glass-panel w-[320px] max-h-[70vh] overflow-y-auto" data-testid="notification-panel">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="font-display text-base p-0">{t('notif.title')}</DropdownMenuLabel>
          {(nearby.length > 0 || notifications.length > 0) && (
            <button
              data-testid="notif-clear-btn"
              onClick={clearAll}
              className="text-xs font-medium text-brand-slate hover:text-[#C86F53] transition px-2 py-1 rounded-lg hover:bg-black/5"
            >
              {t('notif.clear')}
            </button>
          )}
        </div>
        <DropdownMenuSeparator />

        {!hasAny && (
          <div className="px-3 py-8 text-center text-sm text-brand-slate flex flex-col items-center gap-2" data-testid="notif-empty">
            <Inbox className="w-6 h-6 text-brand-dim" />
            {t('notif.empty')}
          </div>
        )}

        {tickets.length > 0 && (
          <div className="px-1 pb-1" data-testid="notif-tickets">
            <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-[#C86F53] font-semibold">{t('notif.ticketsTitle')}</div>
            {tickets.map((tk) => (
              <Link
                key={tk.case_id}
                to="/admin/moderation"
                data-testid={`notif-ticket-${tk.case_id}`}
                onClick={() => setOpen(false)}
                className="flex items-start gap-2 rounded-xl px-2 py-2 hover:bg-black/5 transition"
              >
                <Flag className="w-4 h-4 mt-0.5 text-[#C86F53] shrink-0" />
                <span className="text-sm text-brand-ink line-clamp-2">{tk.title}</span>
              </Link>
            ))}
          </div>
        )}

        {nearby.length > 0 && (
          <div className="px-1 pb-1" data-testid="notif-nearby">
            <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-brand-dim font-semibold">{t('notif.nearbyTitle')}</div>
            {nearby.map((n) => (
              <button
                key={n.pin_id}
                data-testid={`notif-nearby-${n.pin_id}`}
                onClick={() => { onSelectPin?.(n.pin_id); setOpen(false); }}
                className="w-full text-left flex items-start gap-2 rounded-xl px-2 py-2 hover:bg-black/5 transition"
              >
                <CatIcon category={n.category} className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-brand-ink truncate">{n.title}</span>
                  <span className="block text-xs text-brand-dim flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> {t('notif.milesAway', { n: n.distance_miles })}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}

        {notifications.length > 0 && (
          <div className="px-1 pb-1" data-testid="notif-account">
            <div className="px-2 py-1 text-[11px] uppercase tracking-wider text-brand-dim font-semibold">{t('notif.accountTitle')}</div>
            {notifications.map((nt, i) => {
              const inner = (
                <span className="flex items-start gap-2">
                  <Bell className="w-4 h-4 mt-0.5 text-brand-slate shrink-0" />
                  <span className="text-sm text-brand-ink line-clamp-3">{nt.message}</span>
                </span>
              );
              return nt.pin_id ? (
                <button key={nt.id || i} data-testid={`notif-account-${i}`} onClick={() => { onSelectPin?.(nt.pin_id); setOpen(false); }}
                  className="w-full text-left rounded-xl px-2 py-2 hover:bg-black/5 transition">{inner}</button>
              ) : (
                <div key={nt.id || i} data-testid={`notif-account-${i}`} className="rounded-xl px-2 py-2">{inner}</div>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
